import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useRefreshBilling } from '../../api/queries/billing'
import { Button } from '../../ui/Button'
import styles from './Billing.module.css'

const MESSAGES: Record<string, string> = {
  success: 'Payment received. Your plan updates as soon as Stripe confirms it, usually within a few seconds.',
  credits: 'Payment received. Your credits are added as soon as Stripe confirms the payment, usually within a few seconds.',
  cancelled: 'Checkout cancelled. Nothing was charged.',
}

/**
 * Stripe sends the user back to `/app?billing=success|credits|cancelled`.
 *
 * The copy promises only what is true at that moment: the money has been
 * taken, and the entitlement arrives by webhook. It is not written as if the
 * credits were already there, because for a second or two they are not. The
 * status query is refreshed a few times so the figures catch up without a reload.
 */
export function BillingReturnNotice() {
  const [params, setParams] = useSearchParams()
  const refresh = useRefreshBilling()
  // Read once: the return from Stripe is a full page load, so the shell mounts
  // fresh with the key in the URL. The key is then stripped so a reload or a
  // shared link does not announce a payment twice.
  const outcome = params.get('billing')
  const [message, setMessage] = useState<string | null>(() => (outcome ? (MESSAGES[outcome] ?? null) : null))

  useEffect(() => {
    if (!outcome) return
    const next = new URLSearchParams(params)
    next.delete('billing')
    setParams(next, { replace: true })
    const timers = [0, 3000, 8000].map((ms) => window.setTimeout(() => void refresh(), ms))
    return () => timers.forEach(clearTimeout)
    // Runs once per arrival; `params` changes when we strip the key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcome])

  if (!message) return null
  return (
    <div className={styles.returnNotice} role="status">
      {message}
      <Button onClick={() => setMessage(null)}>Dismiss</Button>
    </div>
  )
}
